/**
 * fileValidator.ts
 *
 * Provides comprehensive file validation utilities for S3 uploads.
 * Validates file size, type, naming conventions, and security constraints.
 */

import { UploadOptions } from '../types/s3';
import { validateS3Key } from './s3PathValidator';
import { S3ServiceError } from '../services/S3Service';

// Constants for validation limits (align with UploadOptions defaults)
const DEFAULT_MAX_FILE_SIZE = 5 * 1024 * 1024 * 1024; // 5GB
const DEFAULT_MAX_TOTAL_SIZE = 10 * 1024 * 1024 * 1024; // 10GB
const MAX_S3_KEY_LENGTH = 1024; // S3 key max length

/**
 * Result of file validation
 */
export interface ValidationResult {
  /** Whether the validation passed */
  isValid: boolean;
  /** Array of validation error messages (empty if valid) */
  errors: string[];
  /** Array of validation warning messages */
  warnings: string[];
  /** The validated/sanitized S3 key if validation passed */
  sanitizedKey?: string;
}

/**
 * Check if a filename has a valid extension
 * @param filename The filename to check
 * @param allowedExtensions List of allowed extensions (e.g., ['.pdf', '.jpg'])
 * @returns Whether the filename has an allowed extension
 */
export function isValidExtension(filename: string, allowedExtensions: string[]): boolean {
  if (!allowedExtensions || allowedExtensions.length === 0) {
    return true; // If no restrictions, any extension is valid
  }

  const lowerFilename = filename.toLowerCase();
  return allowedExtensions.some((ext) => {
    const normalizedExt = ext.toLowerCase();
    return lowerFilename.endsWith(normalizedExt);
  });
}

/**
 * Check if a filename is blocked based on extension
 * @param filename The filename to check
 * @param blockedExtensions List of blocked extensions (e.g., ['.exe', '.sh'])
 * @returns Whether the filename has a blocked extension
 */
export function isBlockedExtension(filename: string, blockedExtensions?: string[]): boolean {
  if (!blockedExtensions || blockedExtensions.length === 0) {
    return false;
  }

  const lowerFilename = filename.toLowerCase();
  return blockedExtensions.some((ext) => {
    const normalizedExt = ext.toLowerCase();
    return lowerFilename.endsWith(normalizedExt);
  });
}

/**
 * Validate a single file against the specified options
 * Checks size, extension, and filename constraints
 * @param file The File object to validate
 * @param options Upload options containing validation constraints
 * @returns ValidationResult with any errors or warnings
 */
export function validateFile(file: File, options: UploadOptions = {}): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Get effective limits
  const maxFileSize = options.maxFileSize ?? DEFAULT_MAX_FILE_SIZE;

  // Validate file object
  if (!file || !(file instanceof File)) {
    errors.push('Invalid file object provided');
    return { isValid: false, errors, warnings };
  }

  // Validate file size
  if (file.size === 0) {
    warnings.push('File is empty (0 bytes)');
  }

  if (file.size > maxFileSize) {
    const maxSizeGb = (maxFileSize / (1024 * 1024 * 1024)).toFixed(2);
    const fileSizeGb = (file.size / (1024 * 1024 * 1024)).toFixed(2);
    errors.push(
      `File size ${fileSizeGb}GB exceeds maximum allowed size of ${maxSizeGb}GB`
    );
  }

  // Validate file extension
  if (options.blockedExtensions && isBlockedExtension(file.name, options.blockedExtensions)) {
    errors.push(
      `File extension is not allowed. Blocked extensions: ${options.blockedExtensions.join(', ')}`
    );
  }

  if (options.allowedExtensions && !isValidExtension(file.name, options.allowedExtensions)) {
    errors.push(
      `File extension is not allowed. Allowed extensions: ${options.allowedExtensions.join(', ')}`
    );
  }

  // If we have validation errors, return early
  if (errors.length > 0) {
    return { isValid: false, errors, warnings };
  }

  // Validate filename and S3 key constraints
  // Construct S3 key from filename
  const s3Key = file.name;

  // Check S3 key length constraint
  if (s3Key.length > MAX_S3_KEY_LENGTH) {
    errors.push(
      `S3 key length ${s3Key.length} exceeds maximum allowed length of ${MAX_S3_KEY_LENGTH}`
    );
    return { isValid: false, errors, warnings };
  }

  // Validate the S3 key (this will check for path traversal, invalid chars, etc.)
  try {
    validateS3Key(s3Key);
  } catch (error) {
    if (error instanceof S3ServiceError) {
      errors.push(`Invalid filename for S3: ${error.message}`);
    } else {
      errors.push(`Invalid filename for S3: ${String(error)}`);
    }
    return { isValid: false, errors, warnings };
  }

  return {
    isValid: true,
    errors,
    warnings,
    sanitizedKey: s3Key,
  };
}

/**
 * Validate a batch of files and their cumulative size
 * @param files Array of File objects to validate
 * @param options Upload options containing validation constraints
 * @returns ValidationResult for the batch
 */
export function validateFileBatch(files: File[], options: UploadOptions = {}): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Get effective limits
  const maxFileSize = options.maxFileSize ?? DEFAULT_MAX_FILE_SIZE;
  const maxTotalSize = options.maxTotalSize ?? DEFAULT_MAX_TOTAL_SIZE;

  // Validate files array
  if (!Array.isArray(files)) {
    errors.push('Files must be an array');
    return { isValid: false, errors, warnings };
  }

  if (files.length === 0) {
    errors.push('No files provided for upload');
    return { isValid: false, errors, warnings };
  }

  // Validate individual files and accumulate batch metrics
  let totalSize = 0;
  const invalidFileIndices: number[] = [];

  files.forEach((file, index) => {
    const fileValidation = validateFile(file, options);
    if (!fileValidation.isValid) {
      invalidFileIndices.push(index);
      fileValidation.errors.forEach((err) => {
        errors.push(`File ${index + 1} (${file.name}): ${err}`);
      });
    }
    if (fileValidation.warnings.length > 0) {
      fileValidation.warnings.forEach((warn) => {
        warnings.push(`File ${index + 1} (${file.name}): ${warn}`);
      });
    }
    totalSize += file.size;
  });

  // If any individual files are invalid, return early
  if (invalidFileIndices.length > 0) {
    return { isValid: false, errors, warnings };
  }

  // Check total batch size
  if (totalSize > maxTotalSize) {
    const totalSizeGb = (totalSize / (1024 * 1024 * 1024)).toFixed(2);
    const maxSizeGb = (maxTotalSize / (1024 * 1024 * 1024)).toFixed(2);
    errors.push(
      `Total batch size ${totalSizeGb}GB exceeds maximum allowed batch size of ${maxSizeGb}GB`
    );
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Get human-readable file size string
 * @param bytes Number of bytes
 * @returns Formatted file size string (e.g., "1.5 MB")
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Extract file extension from filename
 * @param filename The filename
 * @returns The file extension (including the dot) or empty string
 */
export function getFileExtension(filename: string): string {
  const lastDotIndex = filename.lastIndexOf('.');
  if (lastDotIndex === -1 || lastDotIndex === 0) {
    return '';
  }
  return filename.substring(lastDotIndex);
}

/**
 * Get file extension without the dot
 * @param filename The filename
 * @returns The file extension without the dot or empty string
 */
export function getFileExtensionWithoutDot(filename: string): string {
  return getFileExtension(filename).replace(/^\./, '');
}

/**
 * Sanitize a filename to prevent security issues and ensure S3 compatibility
 * 
 * Removes or replaces:
 * - Path traversal sequences (../, ..\, ..\\, .\)
 * - Leading/trailing spaces
 * - Control characters
 * - Excessive spaces
 * - Invalid special characters for S3
 * 
 * @param filename The filename to sanitize
 * @returns The sanitized filename
 */
export function sanitizeFilename(filename: string): string {
  if (!filename || typeof filename !== 'string') {
    return '';
  }

  let sanitized = filename;

  // Remove path traversal sequences (.., ../, ..\ etc)
  // Replace all variations to prevent bypasses
  sanitized = sanitized.replace(/\.\.\//g, '');
  sanitized = sanitized.replace(/\.\.\\/g, '');
  sanitized = sanitized.replace(/\.\.\\/g, '');
  sanitized = sanitized.replace(/^\.\//g, '');
  sanitized = sanitized.replace(/^\.\\/g, '');
  sanitized = sanitized.replace(/\.\//g, '_');
  sanitized = sanitized.replace(/\.\\/g, '_');

  // Remove leading/trailing slashes
  sanitized = sanitized.replace(/^\/+/, '');
  sanitized = sanitized.replace(/\/+$/, '');
  sanitized = sanitized.replace(/^\\+/, '');
  sanitized = sanitized.replace(/\\+$/, '');

  // Replace backslashes with underscores (Windows path separators)
  sanitized = sanitized.replace(/\\/g, '_');

  // Replace multiple consecutive slashes with single slash
  sanitized = sanitized.replace(/\/+/g, '/');

  // Remove null bytes and control characters
  sanitized = sanitized.replace(/[\x00-\x1F\x7F]/g, '');

  // Remove leading/trailing whitespace
  sanitized = sanitized.trim();

  // Replace excessive spaces with single space
  sanitized = sanitized.replace(/\s+/g, ' ');

  // Replace invalid characters for S3 with underscores
  // Keep: alphanumeric, dots, dashes, underscores, spaces, slashes
  // Replace: < > : " | ? * and other special chars
  const invalidCharsRegex = /[<>:"|?*\[\]\\]/g;
  sanitized = sanitized.replace(invalidCharsRegex, '_');

  // Ensure filename is not empty after sanitization
  if (!sanitized) {
    return 'file';
  }

  // Ensure filename doesn't exceed S3 key length after sanitization
  // Leave some margin for potential path prefixes
  const maxLength = 512; // Well under 1024 S3 limit
  if (sanitized.length > maxLength) {
    // Preserve extension
    const extension = getFileExtension(sanitized);
    const nameWithoutExt = sanitized.substring(0, sanitized.lastIndexOf('.'));
    const truncatedName = nameWithoutExt.substring(0, maxLength - extension.length);
    sanitized = truncatedName + extension;
  }

  return sanitized;
}

/**
 * Validate a filename against sanitization rules
 * Returns whether the filename needs sanitization
 * 
 * @param filename The filename to check
 * @returns Whether the filename needs sanitization
 */
export function needsSanitization(filename: string): boolean {
  if (!filename || typeof filename !== 'string') {
    return true;
  }

  // Check for path traversal sequences
  if (filename.includes('../') || filename.includes('..\\')) {
    return true;
  }

  // Check for leading/trailing slashes
  if (filename.startsWith('/') || filename.startsWith('\\')) {
    return true;
  }

  // Check for backslashes
  if (filename.includes('\\')) {
    return true;
  }

  // Check for control characters
  if (/[\x00-\x1F\x7F]/.test(filename)) {
    return true;
  }

  // Check for invalid S3 characters
  if (/[<>:"|?*\[\]]/.test(filename)) {
    return true;
  }

  // Check for excessive whitespace
  if (/\s{2,}/.test(filename)) {
    return true;
  }

  // Check for leading/trailing whitespace
  if (filename !== filename.trim()) {
    return true;
  }

  return false;
}

/**
 * Validate a filename for security after sanitization
 * Ensures the sanitized filename will pass S3 validation
 * 
 * @param filename The filename to validate
 * @returns ValidationResult with any security concerns
 */
export function validateFilenameForSecurity(filename: string): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!filename || typeof filename !== 'string') {
    errors.push('Filename must be a non-empty string');
    return { isValid: false, errors, warnings };
  }

  // Check if sanitization is needed
  if (needsSanitization(filename)) {
    const sanitized = sanitizeFilename(filename);
    if (sanitized !== filename) {
      warnings.push(`Filename was modified for security: "${filename}" -> "${sanitized}"`);
    }
  }

  // Check for suspicious patterns that might bypass sanitization
  // Double encoding attempts
  if (filename.includes('%2e%2e') || filename.includes('%2E%2E')) {
    errors.push('Filename contains URL-encoded path traversal sequences');
  }

  // Unicode null bytes or similar tricks
  if (filename.includes('\u0000') || filename.includes('\uFEFF')) {
    errors.push('Filename contains null bytes or BOM characters');
  }

  // Very long filenames (even if not exceeding 1024)
  const sanitized = sanitizeFilename(filename);
  if (sanitized.length > 512) {
    warnings.push(`Filename is very long (${sanitized.length} chars after sanitization)`);
  }

  // Check for reserved names on Windows/Linux that might cause issues
  const reservedNames = [
    'CON', 'PRN', 'AUX', 'NUL',
    'COM1', 'COM2', 'COM3', 'COM4', 'COM5', 'COM6', 'COM7', 'COM8', 'COM9',
    'LPT1', 'LPT2', 'LPT3', 'LPT4', 'LPT5', 'LPT6', 'LPT7', 'LPT8', 'LPT9',
  ];

  const nameWithoutExt = sanitized.substring(0, sanitized.lastIndexOf('.')).toUpperCase();
  if (reservedNames.includes(nameWithoutExt)) {
    warnings.push(`Filename uses a reserved system name: ${nameWithoutExt}`);
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    sanitizedKey: sanitized,
  };
}

/**
 * Safely construct an S3 key from filename with optional prefix
 * Handles sanitization and path safety
 * 
 * @param filename The original filename
 * @param prefix Optional S3 prefix/folder path
 * @returns The safe S3 key ready for validation
 */
export function constructSafeS3Key(filename: string, prefix?: string): string {
  // Sanitize the filename
  let safeFilename = sanitizeFilename(filename);

  // If no prefix, return just the sanitized filename
  if (!prefix) {
    return safeFilename;
  }

  // Sanitize and clean the prefix
  let safePrefix = prefix.trim();

  // Remove leading/trailing slashes from prefix for joining
  safePrefix = safePrefix.replace(/^\/+/, '').replace(/\/+$/, '');

  // If prefix is empty after cleaning, return just filename
  if (!safePrefix) {
    return safeFilename;
  }

  // Check for path traversal in prefix
  if (safePrefix.includes('../') || safePrefix.includes('..\\')) {
    // Strip them out
    safePrefix = safePrefix.replace(/\.\.\//g, '').replace(/\.\.\\/g, '');
  }

  // Combine safely
  return `${safePrefix}/${safeFilename}`;
}

/**
 * Format upload speed in bytes per second to human-readable format
 * @param bytesPerSecond Upload speed in bytes per second
 * @returns Formatted upload speed string (e.g., "1.5 MB/s")
 */
export function formatUploadSpeed(bytesPerSecond: number): string {
  if (bytesPerSecond === 0) return '0 B/s';

  const k = 1024;
  const sizes = ['B/s', 'KB/s', 'MB/s', 'GB/s', 'TB/s'];
  const i = Math.floor(Math.log(bytesPerSecond) / Math.log(k));

  return parseFloat((bytesPerSecond / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Format time remaining in seconds to human-readable format
 * @param seconds Time remaining in seconds (or null if unknown)
 * @returns Formatted time string (e.g., "2h 30m", "45s", or "Unknown")
 */
export function formatTimeRemaining(seconds: number | null): string {
  if (!seconds || seconds === 0) return 'Unknown';

  if (seconds < 60) {
    return `${Math.ceil(seconds)}s`;
  }

  if (seconds < 3600) {
    const minutes = Math.ceil(seconds / 60);
    return `${minutes}m`;
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.ceil((seconds % 3600) / 60);

  if (minutes === 0) {
    return `${hours}h`;
  }

  return `${hours}h ${minutes}m`;
}
